import { useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import * as tus from "tus-js-client";
import {
  CurrentPlatformQuery,
  useCurrentPlatformQuery,
} from "@/partner/entities/platforms";
import type { CurrentPlatformQueryType } from "@/types/graphql/partner.generated";
import {
  useRequestTusVideoUploadMutation,
  useRequestVideoUploadMutation,
  useCreateMultipleContentsMutation,
} from "@/partner/entities/contents";
import { formatFileSize } from "@/shared/utils/formatFileSize";
import { buildTusOptions } from "./buildTusOptions";
import { MAX_UPLOAD_FILE_SIZE_BYTES } from "./uploadLimits";
import { readVideoFileDuration } from "./readVideoFileDuration";

export interface UploadItem {
  key: string;
  fileName: string;
  progress: number;
  status: "uploading" | "done" | "error" | "canceled";
  videoUid: string | null;
  /** object URL исходного файла — для 16:9 видео-превью строки в UploadsDialog */
  fileUrl: string;
}

// 50 MiB. Кратно 256 KiB: требование Cloudflare. Bunny кратности не
// требует, поэтому значение общее для обеих веток.
const CHUNK_SIZE = 52428800;

export const useMultipleUploads = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<UploadItem[]>([]);
  // true пока идёт createMultipleContents по подтверждённой ("Далее") пачке
  const [creating, setCreating] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const apolloClient = useApolloClient();
  // Хук здесь нужен только чтобы держать запрос "тёплым" (кэш заполнен к
  // моменту uploadOne()): реальное значение провайдера читается там же,
  // через client.query(), которое дожидается результата, а не берёт
  // возможно ещё не разрешившийся снимок из рендера.
  useCurrentPlatformQuery();
  const [requestVideoUpload] = useRequestVideoUploadMutation();
  const [requestTusVideoUpload] = useRequestTusVideoUploadMutation();
  const [createMultipleContents] = useCreateMultipleContentsMutation({
    refetchQueries: ["ContentsList"],
  });

  // key -> активный tus.Upload, чтобы можно было отменить конкретный файл
  const uploadsRef = useRef<Map<string, tus.Upload>>(new Map());
  // key -> resolve промиса ожидания завершения загрузки (нужен, т.к. abort()
  // не всегда вызывает onError, и без этого await в uploadOne повис бы)
  const cancelResolversRef = useRef<Map<string, () => void>>(new Map());
  // ключи, отменённые пользователем — чтобы отличить отмену от ошибки/успеха
  const canceledKeysRef = useRef<Set<string>>(new Set());
  // key -> object URL файла (для превью и последующего revoke при clear())
  const fileUrlsRef = useRef<Map<string, string>>(new Map());
  // ссылка на актуальные items — нужна в confirmCreate, который не должен
  // пересоздаваться при каждом патче прогресса
  const itemsRef = useRef<UploadItem[]>([]);
  itemsRef.current = items;

  const patchItem = (key: string, patch: Partial<UploadItem>) => {
    setItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, ...patch } : item))
    );
  };

  const uploadOne = async (file: File, key: string): Promise<void> => {
    // См. uploadLimits.ts: файл сверх предела провайдера отклоняем сразу, а
    // не после долгой закачки, особенно при массовой (drag&drop) загрузке.
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      console.error(`Video upload rejected: file too large`, {
        fileName: file.name,
        fileSize: file.size,
      });
      patchItem(key, { status: "error" });
      enqueueSnackbar(
        t("videos.uploads.fileTooLargeError", {
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          maxSize: formatFileSize(MAX_UPLOAD_FILE_SIZE_BYTES),
        }),
        { variant: "error" }
      );
      return;
    }

    try {
      // Провайдер платформы обязателен для выбора ветки ниже, а хук на
      // useCurrentPlatformQuery() выше может ещё не разрешиться (сразу
      // после монтирования страницы, при drag&drop первого файла). Гадать
      // на Cloudflare нельзя: на Bunny-платформе requestVideoUpload
      // бросает VIDEO_UPLOAD_NOT_SUPPORTED без повтора. client.query() с
      // cache-first либо мгновенно отдаёт уже прогретый кэш, либо
      // дожидается того же запроса, что и хук.
      const { data: platformData } =
        await apolloClient.query<CurrentPlatformQueryType>({
          query: CurrentPlatformQuery,
          fetchPolicy: "cache-first",
        });

      // У провайдеров с прямым upload URL (Cloudflare) бэкенд отдаёт уже
      // созданный tus-ресурс, поэтому его адрес идёт в uploadUrl.
      // У провайдеров с подписанными заголовками (Bunny) ресурс создаёт
      // сам tus-клиент на общем endpoint, а авторизация лежит в headers.
      const isTusProvider =
        platformData?.currentPlatform?.effectiveVideoProvider === "BUNNY";

      let prepared: ReturnType<typeof buildTusOptions>;

      if (isTusProvider) {
        const { data } = await requestTusVideoUpload({
          variables: {
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type || "video/mp4",
          },
        });
        const payload = data?.requestTusVideoUpload;
        if (!payload) throw new Error("no upload url");
        prepared = buildTusOptions({
          file,
          tus: {
            endpoint: String(payload.endpoint),
            videoUid: payload.videoUid,
            headers: payload.headers,
          },
        });
      } else {
        // Реальная длина файла, см. useVideoUpload.ts: без неё Cloudflare
        // резервирует под каждую параллельную загрузку часы хранения и
        // пачка файлов упирается в квоту плана (413).
        const durationSeconds = await readVideoFileDuration(file);
        const { data } = await requestVideoUpload({
          variables: { fileSize: file.size, durationSeconds },
        });
        const payload = data?.requestVideoUpload;
        if (!payload) throw new Error("no upload url");
        prepared = buildTusOptions({
          file,
          direct: {
            uploadUrl: String(payload.uploadUrl),
            videoUid: payload.videoUid,
          },
        });
      }

      if (canceledKeysRef.current.has(key)) {
        canceledKeysRef.current.delete(key);
        return;
      }

      await new Promise<void>((resolve, reject) => {
        const upload = new tus.Upload(file, {
          ...prepared.options,
          chunkSize: CHUNK_SIZE,
          // Как в референсном useUpload.js — более терпеливый бэкофф важен
          // именно для крупных файлов на нестабильном канале.
          retryDelays: [0, 3000, 5000, 10000, 20000, 50000, 100000],
          onProgress: (uploaded, total) =>
            patchItem(key, { progress: Math.round((uploaded / total) * 100) }),
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
        uploadsRef.current.set(key, upload);
        cancelResolversRef.current.set(key, () => resolve());
        upload.start();
      });

      uploadsRef.current.delete(key);
      cancelResolversRef.current.delete(key);

      if (canceledKeysRef.current.has(key)) {
        canceledKeysRef.current.delete(key);
        return;
      }

      patchItem(key, {
        status: "done",
        progress: 100,
        videoUid: prepared.videoUid,
      });
    } catch (error) {
      uploadsRef.current.delete(key);
      cancelResolversRef.current.delete(key);

      if (canceledKeysRef.current.has(key)) {
        canceledKeysRef.current.delete(key);
        return;
      }

      console.error("Video upload failed", error);
      patchItem(key, { status: "error" });
    }
  };

  const cancelItem = (key: string) => {
    canceledKeysRef.current.add(key);

    const upload = uploadsRef.current.get(key);
    if (upload) {
      upload.abort(true).catch(() => {
        // Игнорируем ошибку прерывания — файл в любом случае считается отменённым
      });
      uploadsRef.current.delete(key);
    }

    patchItem(key, { status: "canceled" });

    const resolveWaiting = cancelResolversRef.current.get(key);
    if (resolveWaiting) {
      resolveWaiting();
      cancelResolversRef.current.delete(key);
    }
  };

  // Загрузка файлов НЕ создаёт записи контента сама по себе — как в референсе
  // (UploadDialog.js), запись создаётся только по явному подтверждению
  // ("Далее" в UploadsDialog, см. confirmCreate) после того, как все загрузки
  // завершились (успешно или с ошибкой).
  const startUploads = (files: File[]) => {
    const initial: UploadItem[] = files.map((file, index) => {
      const key = `${Date.now()}-${index}-${file.name}`;
      const fileUrl = URL.createObjectURL(file);
      fileUrlsRef.current.set(key, fileUrl);
      return {
        key,
        fileName: file.name,
        progress: 0,
        status: "uploading" as const,
        videoUid: null,
        fileUrl,
      };
    });
    setItems((prev) => [...prev, ...initial]);

    // uploadOne никогда не бросает исключение наружу (все ошибки уходят в
    // patchItem({status:"error"})), поэтому лишний .catch() не нужен.
    void Promise.all(
      files.map((file, index) => uploadOne(file, initial[index].key))
    );
  };

  const confirmCreate = async () => {
    const succeeded = itemsRef.current.filter(
      (item) => item.status === "done" && item.videoUid
    );

    if (succeeded.length === 0) {
      clear();
      return;
    }

    setCreating(true);
    try {
      await createMultipleContents({
        variables: {
          input: succeeded.map((item) => ({
            title: item.fileName.replace(/\.[^.]+$/, ""),
            videoUid: item.videoUid,
            videoFileName: item.fileName,
          })),
        },
      });
      clear();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      enqueueSnackbar(
        t("videos.uploads.createRecordsError", { error: errorMessage }),
        { variant: "error" }
      );
    } finally {
      setCreating(false);
    }
  };

  // Как референсный resetUploads() (useMultipleUploads.js): диалог теперь
  // закрываем в любой момент (кнопка закрытия больше не ждёт allDone), так
  // что ещё идущие загрузки нужно аварийно прервать, а не бросить в фоне.
  const clear = () => {
    itemsRef.current.forEach((item) => {
      if (item.status === "uploading") {
        cancelItem(item.key);
      }
    });
    fileUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    fileUrlsRef.current.clear();
    setItems([]);
  };

  return { items, startUploads, clear, cancelItem, confirmCreate, creating };
};
